import type { NumberValue } from 'd3-scale';

import { charsFontWidth12pixelsSize400Map } from '../components/text-ellipsis/chars-font-width-map.js';
import { calculateWidth } from '../components/text-ellipsis/truncate-string.js';
import type { AnyScale } from '../types/scales.js';

/**
 * Minimum pixel gap required between adjacent tick labels.
 *
 * Lower values keep more ticks visible at the risk of labels almost touching;
 * higher values trigger reduction sooner and leave more whitespace. ~6px keeps
 * the gap visually clear while avoiding asymmetric drops between sibling
 * charts whose labels differ only by a few pixels (e.g. a leading "-").
 */
export const TICK_LABEL_GAP_PX = 6;

export type ContinuousScale = Exclude<AnyScale, { bandwidth: () => number }>;

/**
 * Detects whether any two adjacent formatted tick labels would visually
 * overlap on a horizontal axis.
 *
 * When `domainBounds` is provided the function uses domain-aware alignment —
 * the same logic as `getBottomTickLabelPropsWithDomainAlignment` (AC1):
 * - tick value === domainBounds[0] → `start`  (label extends right of position)
 * - tick value === domainBounds[1] → `end`    (label extends left of position)
 * - anything else                 → `middle`  (label centered on position)
 *
 * When `domainBounds` is omitted the function falls back to the legacy
 * index-based behaviour (first=start, last=end, others=middle), which is
 * kept for callers that have not been migrated yet.
 */
export function hasHorizontalLabelCollision(
  formattedTicks: string[],
  rawTicks: NumberValue[],
  scale: ContinuousScale,
  domainBounds?: [number, number],
): boolean {
  // Empty-label ticks (e.g. minor ticks on log scales) render no visible text.
  // Filtering them out prevents false-positive collisions where a zero-width
  // invisible label would still trigger the gap check against a real label.
  const nonEmptyIndices = formattedTicks.reduce<number[]>((acc, label, i) => {
    if (label !== '') {
      acc.push(i);
    }
    return acc;
  }, []);
  const filteredLabels = nonEmptyIndices.map((i) => formattedTicks[i]);
  const filteredRaw = nonEmptyIndices.map((i) => rawTicks[i]);

  const n = filteredLabels.length;
  if (n < 2) {
    return false;
  }

  const positionAt = scale;
  const bounds: { left: number; right: number }[] = new Array(n);

  for (let i = 0; i < n; i++) {
    const width = calculateWidth(
      filteredLabels[i],
      charsFontWidth12pixelsSize400Map,
    );
    const pos = positionAt(filteredRaw[i]);

    let anchor: 'start' | 'middle' | 'end';
    if (domainBounds !== undefined) {
      const numericValue = +filteredRaw[i];
      if (numericValue === domainBounds[0]) {
        anchor = 'start';
      } else if (numericValue === domainBounds[1]) {
        anchor = 'end';
      } else {
        anchor = 'middle';
      }
    } else {
      anchor = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle';
    }

    if (anchor === 'start') {
      bounds[i] = { left: pos, right: pos + width };
    } else if (anchor === 'end') {
      bounds[i] = { left: pos - width, right: pos };
    } else {
      bounds[i] = { left: pos - width / 2, right: pos + width / 2 };
    }
  }

  for (let i = 0; i < n - 1; i++) {
    if (bounds[i].right + TICK_LABEL_GAP_PX > bounds[i + 1].left) {
      return true;
    }
  }
  return false;
}
